import { Button, Flex, Text } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { BLUEMONEDDBUTTONS } from "../../../../constants/colors";

// Paginação das tabelas do extrato.
//
// Por que existe: a consulta do extrato traz o período inteiro numa resposta só
// (não há paginação no servidor porque a tela agrupa por tipo de lançamento e
// precisa do período completo para os totais de cada bloco fecharem). Num
// cliente movimentado isso passa de 20 mil linhas em 31 dias — renderizar tudo
// de uma vez monta centenas de milhares de nós e trava a aba, já que o render
// do React é síncrono.
//
// A paginação aqui é LOCAL: os dados continuam vindo inteiros e os totais
// continuam sendo calculados sobre a lista completa. O que muda é quantas
// linhas existem no DOM ao mesmo tempo.

export const LINHAS_POR_PAGINA = 50;

export function usePaginaLocal<T>(data: T[] | undefined, tamanho: number = LINHAS_POR_PAGINA) {
  const lista = useMemo(() => data ?? [], [data]);
  const [pagina, setPagina] = useState(1);

  const totalPaginas = Math.max(1, Math.ceil(lista.length / tamanho));

  // troca de período (ou lista que encolheu) não pode deixar a tela parada
  // numa página que deixou de existir
  useEffect(() => {
    setPagina(1);
  }, [lista]);

  const inicio = (pagina - 1) * tamanho;
  const itens = useMemo(
    () => lista.slice(inicio, inicio + tamanho),
    [lista, inicio, tamanho]
  );

  return { pagina, setPagina, itens, totalPaginas, total: lista.length, inicio, tamanho };
}

interface PaginacaoTabelaProps {
  pagina: number;
  totalPaginas: number;
  total: number;
  inicio: number;
  tamanho: number;
  setPagina: (p: number) => void;
}

export default function PaginacaoTabela({
  pagina,
  totalPaginas,
  total,
  inicio,
  tamanho,
  setPagina,
}: PaginacaoTabelaProps) {
  // uma página só: não polui a tela com controle que não faz nada
  if (totalPaginas <= 1) return null;

  const fim = Math.min(inicio + tamanho, total);

  return (
    <Flex justifyContent="flex-end" alignItems="center" gap={3} mt={2} mb={4} px={2}>
      <Text fontSize="12px" color="gray.600">
        {inicio + 1}–{fim} de {total.toLocaleString("pt-BR")}
      </Text>
      <Button
        size="xs"
        bgColor={BLUEMONEDDBUTTONS}
        color="white"
        isDisabled={pagina <= 1}
        onClick={() => setPagina(pagina - 1)}
      >
        Anterior
      </Button>
      <Text fontSize="12px" color="gray.600">
        {pagina} / {totalPaginas}
      </Text>
      <Button
        size="xs"
        bgColor={BLUEMONEDDBUTTONS}
        color="white"
        isDisabled={pagina >= totalPaginas}
        onClick={() => setPagina(pagina + 1)}
      >
        Próxima
      </Button>
    </Flex>
  );
}
